import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Package, DollarSign, ShoppingBasket, MapPin, Bell, Settings, CreditCard } from 'lucide-react';
import axios from 'axios';
import ProducerNotifications from '../components/ProducerNotifications';
import PaymentMethodsConfig from '../components/PaymentMethodsConfig';

const ProducerDashboard = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    quantity: '',
    category: '',
    description: '',
    location: '',
    // Delivery options
    delivery_available: false,
    pickup_available: true,
    delivery_fee: '',
    delivery_radius: '',
    delivery_notes: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const { API, user } = useContext(AuthContext);

  useEffect(() => {
    fetchMyProducts();
    fetchCategories();
  }, []);

  const fetchMyProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/my`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const submitData = new FormData();
    Object.keys(formData).forEach(key => {
      if (formData[key]) {
        submitData.append(key, formData[key]);
      }
    });
    
    if (imageFile) {
      submitData.append('image', imageFile);
    }

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Produto atualizado com sucesso!');
      } else {
        await axios.post(`${API}/products`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Produto criado com sucesso!');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchMyProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar produto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    const deliveryOptions = product.delivery_options || {};
    setFormData({
      name: product.name,
      price: product.price.toString(),
      quantity: product.quantity.toString(),
      category: product.category,
      description: product.description || '',
      location: product.location,
      delivery_available: deliveryOptions.delivery || false,
      pickup_available: deliveryOptions.pickup !== false,
      delivery_fee: deliveryOptions.delivery_fee ? deliveryOptions.delivery_fee.toString() : '',
      delivery_radius: deliveryOptions.delivery_radius ? deliveryOptions.delivery_radius.toString() : '',
      delivery_notes: deliveryOptions.delivery_notes || ''
    });
    setImageFile(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Produto excluído com sucesso!');
      fetchMyProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erro ao excluir produto');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      quantity: '',
      category: '',
      description: '',
      location: '',
      delivery_available: false,
      pickup_available: true,
      delivery_fee: '',
      delivery_radius: '',
      delivery_notes: ''
    });
    setImageFile(null);
    setEditingProduct(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (value) => {
    setFormData({
      ...formData,
      category: value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
    }
  };

  const openDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const totalValue = products.reduce((sum, product) => sum + (product.price * product.quantity), 0);
  const totalProducts = products.reduce((sum, product) => sum + product.quantity, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-orange-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-green-600 mb-2">Dashboard do Produtor</h1>
          <p className="text-gray-600">Gerencie seus produtos e vendas</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="card-feira">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total de Produtos</p>
                  <p className="text-3xl font-bold text-green-600" data-testid="total-products-count">
                    {products.length}
                  </p>
                </div>
                <Package className="w-12 h-12 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-feira">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Estoque Total</p>
                  <p className="text-3xl font-bold text-orange-600" data-testid="total-stock-count">
                    {totalProducts}
                  </p>
                </div>
                <ShoppingBasket className="w-12 h-12 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="card-feira">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Valor Total</p>
                  <p className="text-3xl font-bold text-yellow-600" data-testid="total-value">
                    R$ {totalValue.toFixed(2)}
                  </p>
                </div>
                <DollarSign className="w-12 h-12 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Notifications Section */}
        <div className="mb-8">
          <ProducerNotifications />
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Meus Produtos
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configurações
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products" className="space-y-6">
            {/* Actions */}
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Meus Produtos</h2>
              
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={openDialog} className="btn-feira" data-testid="add-product-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Produto
                  </Button>
                </DialogTrigger>
            
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingProduct ? 'Editar Produto' : 'Novo Produto'}
                </DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome do Produto</Label>
                    <Input
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="input-feira"
                      data-testid="product-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="price">Preço (R$)</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      required
                      value={formData.price}
                      onChange={handleChange}
                      className="input-feira"
                      data-testid="product-price-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="quantity">Quantidade</Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      type="number"
                      required
                      value={formData.quantity}
                      onChange={handleChange}
                      className="input-feira"
                      data-testid="product-quantity-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select value={formData.category} onValueChange={handleSelectChange} data-testid="product-category-select">
                      <SelectTrigger className="input-feira">
                        <SelectValue placeholder="Selecione a categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">Localização (bairro/comunidade)</Label>
                  <Input
                    id="location"
                    name="location"
                    required
                    value={formData.location}
                    onChange={handleChange}
                    className="input-feira"
                    data-testid="product-location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição (opcional)</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="input-feira"
                    data-testid="product-description-input"
                  />
                </div>

                {/* Delivery Options Section */}
                <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <Label className="text-base font-semibold">Opções de Entrega</Label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="pickup_available"
                        name="pickup_available"
                        checked={formData.pickup_available}
                        onChange={(e) => setFormData({...formData, pickup_available: e.target.checked})}
                        className="h-4 w-4 text-green-600"
                      />
                      <Label htmlFor="pickup_available">Retirada no local</Label>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="delivery_available"
                        name="delivery_available"
                        checked={formData.delivery_available}
                        onChange={(e) => setFormData({...formData, delivery_available: e.target.checked})}
                        className="h-4 w-4 text-green-600"
                      />
                      <Label htmlFor="delivery_available">Entrega disponível</Label>
                    </div>
                  </div>
                  
                  {formData.delivery_available && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 p-3 bg-white rounded border">
                      <div className="space-y-2">
                        <Label htmlFor="delivery_fee">Taxa de entrega (R$)</Label>
                        <Input
                          id="delivery_fee"
                          name="delivery_fee"
                          type="number"
                          step="0.01"
                          placeholder="Ex: 5.00"
                          value={formData.delivery_fee}
                          onChange={handleChange}
                          className="input-feira"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label htmlFor="delivery_radius">Raio de entrega (km)</Label>
                        <Input
                          id="delivery_radius"
                          name="delivery_radius"
                          type="number"
                          placeholder="Ex: 10"
                          value={formData.delivery_radius}
                          onChange={handleChange}
                          className="input-feira"
                        />
                      </div>
                      
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="delivery_notes">Observações sobre entrega</Label>
                        <Textarea
                          id="delivery_notes"
                          name="delivery_notes"
                          placeholder="Ex: Entrego apenas nos fins de semana"
                          value={formData.delivery_notes}
                          onChange={handleChange}
                          rows={2}
                          className="input-feira"
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="image">Imagem do Produto</Label>
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="input-feira"
                    data-testid="product-image-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="btn-feira flex-1" data-testid="save-product-button">
                    {editingProduct ? 'Atualizar' : 'Criar'} Produto
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setIsDialogOpen(false)}
                    data-testid="cancel-product-button"
                  >
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
              </Dialog>
            </div>

            {/* Products Grid */}
            {loading ? (
          <div className="products-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className="card-feira p-4">
                <div className="w-full h-48 bg-gray-200 rounded-lg mb-4 skeleton"></div>
                <div className="h-4 bg-gray-200 rounded mb-2 skeleton"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4 skeleton"></div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16" data-testid="no-products-message">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">Nenhum produto cadastrado</h3>
            <p className="text-gray-500 mb-4">Comece adicionando seu primeiro produto para venda.</p>
            <Button onClick={openDialog} className="btn-feira">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Primeiro Produto
            </Button>
          </div>
        ) : (
          <div className="products-grid">
            {products.map((product) => (
              <Card key={product.id} className="card-feira overflow-hidden" data-testid={`product-${product.id}`}>
                <div className="aspect-video relative overflow-hidden">
                  {product.image_url ? (
                    <>
                      <img
                        src={`${product.image_url}`}
                        alt={product.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-gradient-to-br from-green-100 to-orange-100 flex items-center justify-center" style={{display: 'none'}}>
                        <Package className="w-12 h-12 text-green-500" />
                      </div>
                    </>
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-green-100 to-orange-100 flex items-center justify-center">
                      <Package className="w-12 h-12 text-green-500" />
                    </div>
                  )}
                  <Badge className="absolute top-2 left-2 category-badge">
                    {product.category}
                  </Badge>
                </div>

                <CardContent className="p-4">
                  <CardTitle className="text-lg font-bold text-gray-800 mb-2">
                    {product.name}
                  </CardTitle>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between items-center">
                      <span className="text-2xl font-bold text-green-600">
                        R$ {product.price.toFixed(2)}
                      </span>
                      <span className="text-sm text-gray-500">
                        {product.quantity} disponível
                      </span>
                    </div>
                    
                    {product.description && (
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {product.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <MapPin className="w-4 h-4" />
                      <span>{product.location}</span>
                    </div>
                    
                    {/* Delivery Options */}
                    {product.delivery_options && (
                      <div className="flex gap-1 text-xs">
                        {product.delivery_options.pickup && (
                          <Badge variant="outline" className="text-xs">
                            📍 Retirada
                          </Badge>
                        )}
                        {product.delivery_options.delivery && (
                          <Badge variant="outline" className="text-xs">
                            🚚 Entrega
                            {product.delivery_options.delivery_fee && 
                              ` R$${product.delivery_options.delivery_fee.toFixed(2)}`}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(product)}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      data-testid={`edit-product-${product.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      onClick={() => handleDelete(product.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:border-red-300"
                      data-testid={`delete-product-${product.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            </div>
          )}
          </TabsContent>

          <TabsContent value="settings" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Payment Methods Configuration */}
              <PaymentMethodsConfig />
              
              {/* Future settings can be added here */}
              <Card className="card-feira">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-600" />
                    Outras Configurações
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-center py-8">
                    Mais opções de configuração serão adicionadas em breve.
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProducerDashboard;